/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { map } from 'rxjs/operators';

declare var braintree: any;

@Injectable({ providedIn: 'root' })
export class BraintreeService {
  constructor(private http: HttpClient) {}

  getClientToken() {
    return this.http.get(`${environment.apiHostname}/v1/subscriptions/braintree/generate-token`).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  addCustomer(username: string, nonce: string) {
    return this.http.post(`${environment.apiHostname}/v1/purchases/braintree/customer`, { username, nonce }).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  getCustomer(username: string) {
    return this.http.get(`${environment.apiHostname}/v1/purchases/braintree/customer/${username}`).pipe(
      map((response: any) => {
        if (response.result.success) {
          return response.data;
        } else {
          return null;
        }
      })
    );
  }

  createTransaction(username: string, charge: number, paymentMethodToken?: string, saveCard?: boolean) {
    return this.http
      .post(`${environment.apiHostname}/v1/purchases/braintree/transaction`, {
        username,
        amount: charge,
        paymentMethodToken,
        saveInVaultOnSuccess: saveCard,
      })
      .pipe(
        map((response: any) => {
          return response.data;
        })
      );
  }

  findTransaction(transactionId: string) {
    return this.http.get(`${environment.apiHostname}/v1/purchases/braintree/transaction/${transactionId}`).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  createPaymentMethod(username: string, nonce: string) {
    return this.http
      .post(`${environment.apiHostname}/v1/purchases/braintree/customer/payment-method`, { username, nonce })
      .pipe(
        map((response: any) => {
          return response.data;
        })
      );
  }

  updatePaymentMethod(username: string, nonce: string, token: string) {
    return this.http
      .put(`${environment.apiHostname}/v1/purchases/braintree/customer/payment-method`, { username, nonce, token })
      .pipe(
        map((response: any) => {
          return response.data;
        })
      );
  }

  setPrimaryPaymentMethod(token: string) {
    return this.http
      .put(`${environment.apiHostname}/v1/purchases/braintree/customer/payment-method/${token}/make-default`, {})
      .pipe(
        map((response: any) => {
          return response.data;
        })
      );
  }

  deletePaymentMethod(token: string) {
    return this.http.delete(`${environment.apiHostname}/v1/purchases/braintree/payment-method/${token}`).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  initialize(token: string, whiteTheme, callback: any, prefillOptions?: any) {
    const styles = {
      input: {
        padding: '10px',
      },
    };

    braintree.client.create(
      {
        authorization: token,
      },
      (err, clientInstance) => {
        if (err) {
          console.error(err);
          return;
        }

        braintree.hostedFields.create(
          {
            client: clientInstance,
            styles,
            fields: {
              number: {
                selector: '#card-number',
                placeholder: 'Card Number',
                prefill: prefillOptions && prefillOptions.number ? prefillOptions.number : '',
              },
              cvv: {
                selector: '#cvv',
                placeholder: 'CVV',
              },
              expirationDate: {
                selector: '#expirationDate',
                placeholder: 'MM/YY',
                prefill: prefillOptions && prefillOptions.expirationDate ? prefillOptions.expirationDate : '',
              },
            },
          },
          callback
        );
      }
    );
  }

  promiseInitialize(token: string, whiteTheme): Promise<any> {
    return new Promise((resolve, reject) => {
      this.initialize(token, whiteTheme, (err, hostedFieldsInstance) => {
        if (err) {
          reject(err);
          return;
        }
        return resolve(hostedFieldsInstance);
      });
    });
  }
}
